import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, Plus, Edit, Trash2, Tag, Save, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';
import Modal from '../../components/common/Modal';
import { useToast } from '../../components/common/Toast';
import './ToolsManage.css';

const EMPTY_FORM = {
  name: '',
  category_id: '',
  description: '',
  url: '',
  managed_by: '',
  status: 'active',
  subscription_tier: '',
  billing_cycle: '',
  renewal_date: '',
  cost: '',
  notes: '',
};

function ToolsManage() {
  const toast = useToast();
  const location = useLocation();

  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tool form
  const [formOpen, setFormOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTool, setDeleteTool] = useState(null);

  // Category management
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [toolsRes, categoriesRes] = await Promise.allSettled([
        api.get('/tools'),
        api.get('/tools/categories'),
      ]);
      setTools(toolsRes.status === 'fulfilled' ? (toolsRes.value.tools || []) : []);
      setCategories(categoriesRes.status === 'fulfilled' ? (categoriesRes.value.categories || []) : []);
    } catch (err) {
      // Don't loop
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle incoming edit from Tools page navigation
  useEffect(() => {
    if (location.state?.editTool) {
      handleEditClick(location.state.editTool);
      // Clear the state so it does not re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleEditClick = (tool) => {
    setEditingTool(tool);
    setForm({
      name: tool.name || '',
      category_id: tool.category_id || '',
      description: tool.description || '',
      url: tool.url || '',
      managed_by: tool.managed_by || '',
      status: tool.status || 'active',
      subscription_tier: tool.subscription_tier || '',
      billing_cycle: tool.billing_cycle || '',
      renewal_date: tool.renewal_date ? tool.renewal_date.split('T')[0] : '',
      cost: tool.cost !== null && tool.cost !== undefined ? tool.cost : '',
      notes: tool.notes || '',
    });
    setFormOpen(true);
  };

  const handleAddClick = () => {
    setEditingTool(null);
    setForm({ ...EMPTY_FORM });
    setFormOpen(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Tool name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        category_id: form.category_id || null,
        cost: form.cost !== '' ? parseFloat(form.cost) : null,
        billing_cycle: form.billing_cycle || null,
        renewal_date: form.renewal_date || null,
      };

      if (editingTool) {
        await api.put(`/tools/${editingTool.id}`, payload);
        toast.success('Tool updated');
      } else {
        await api.post('/tools', payload);
        toast.success('Tool created');
      }
      setFormOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to save tool');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (tool) => {
    setDeleteTool(tool);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTool) return;
    try {
      await api.delete(`/tools/${deleteTool.id}`);
      toast.success(`${deleteTool.name} deleted`);
      setDeleteModalOpen(false);
      setDeleteTool(null);
      loadData();
    } catch (err) {
      toast.error('Failed to delete tool');
    }
  };

  // Category CRUD
  const handleCategoryAdd = () => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryFormOpen(true);
  };

  const handleCategoryEdit = (cat) => {
    setEditingCategory(cat);
    setCategoryName(cat.name);
    setCategoryFormOpen(true);
  };

  const handleCategorySave = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      if (editingCategory) {
        await api.put(`/tools/categories/${editingCategory.id}`, { name: categoryName.trim() });
        toast.success('Category updated');
      } else {
        await api.post('/tools/categories', { name: categoryName.trim() });
        toast.success('Category created');
      }
      setCategoryFormOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to save category');
    }
  };

  const handleCategoryDelete = async (cat) => {
    if (cat.tool_count > 0) {
      toast.error(`Cannot delete "${cat.name}" - ${cat.tool_count} tool(s) assigned`);
      return;
    }
    try {
      await api.delete(`/tools/categories/${cat.id}`);
      toast.success(`Category "${cat.name}" deleted`);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete category');
    }
  };

  return (
    <div className="tools-manage">
      <div className="page-header">
        <Wrench size={22} strokeWidth={2} />
        <h1>Manage Tools</h1>
        <button className="btn-primary" onClick={handleAddClick}>
          <Plus size={16} />
          Add Tool
        </button>
      </div>

      {/* Category Management */}
      <div className="section-card">
        <div className="section-header">
          <Tag size={16} />
          <h2>Categories</h2>
          <button className="btn-sm" onClick={handleCategoryAdd}>
            <Plus size={14} /> Add
          </button>
        </div>
        <div className="category-list">
          {categories.length === 0 ? (
            <p className="empty-text">No categories yet. Create one to organize your tools.</p>
          ) : (
            categories.map(cat => (
              <div key={cat.id} className="category-item">
                <span className="category-item-name">{cat.name}</span>
                <span className="category-item-count">{cat.tool_count} tool{cat.tool_count !== 1 ? 's' : ''}</span>
                <button className="icon-btn" onClick={() => handleCategoryEdit(cat)} title="Edit">
                  <Edit size={14} />
                </button>
                <button className="icon-btn icon-btn-danger" onClick={() => handleCategoryDelete(cat)} title="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Tools List */}
      <div className="section-card">
        <div className="section-header">
          <Wrench size={16} />
          <h2>Tools ({tools.length})</h2>
        </div>

        {loading ? (
          <p className="empty-text">Loading...</p>
        ) : tools.length === 0 ? (
          <p className="empty-text">No tools yet. Click "Add Tool" to create one.</p>
        ) : (
          <div className="tools-table">
            <div className="tools-table-header">
              <span>Name</span>
              <span>Category</span>
              <span>Status</span>
              <span>Cost</span>
              <span>Actions</span>
            </div>
            {tools.map(tool => (
              <div key={tool.id} className="tools-table-row">
                <div className="tool-name-cell">
                  <span className="tool-table-name">{tool.name}</span>
                  {tool.description && <span className="tool-table-desc">{tool.description}</span>}
                </div>
                <span className="tool-table-category">{tool.category_name || '-'}</span>
                <span className={`tool-table-status status-${tool.status}`}>{tool.status}</span>
                <span className="tool-table-cost">
                  {tool.cost !== null && tool.cost !== undefined ? `$${parseFloat(tool.cost).toFixed(2)}` : '-'}
                </span>
                <div className="tool-table-actions">
                  <button className="icon-btn" onClick={() => handleEditClick(tool)} title="Edit">
                    <Edit size={14} />
                  </button>
                  <button className="icon-btn icon-btn-danger" onClick={() => handleDeleteClick(tool)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tool Form Modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={editingTool ? 'Edit Tool' : 'Add Tool'}
        size="large"
      >
        <form onSubmit={handleFormSubmit} className="tool-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="Tool name"
                required
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category_id} onChange={(e) => handleFormChange('category_id', e.target.value)}>
                <option value="">No category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Brief description"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>URL</label>
              <input
                type="url"
                value={form.url}
                onChange={(e) => handleFormChange('url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Managed By</label>
              <input
                type="text"
                value={form.managed_by}
                onChange={(e) => handleFormChange('managed_by', e.target.value)}
                placeholder="Who owns the vendor relationship"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => handleFormChange('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="evaluating">Evaluating</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>
            <div className="form-group">
              <label>Subscription Tier</label>
              <input
                type="text"
                value={form.subscription_tier}
                onChange={(e) => handleFormChange('subscription_tier', e.target.value)}
                placeholder="e.g., Business, Enterprise"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Billing Cycle</label>
              <select value={form.billing_cycle} onChange={(e) => handleFormChange('billing_cycle', e.target.value)}>
                <option value="">Not specified</option>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="one_time">One-time</option>
                <option value="free">Free</option>
              </select>
            </div>
            <div className="form-group">
              <label>Renewal Date</label>
              <input
                type="date"
                value={form.renewal_date}
                onChange={(e) => handleFormChange('renewal_date', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Cost</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={(e) => handleFormChange('cost', e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Internal notes..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => setFormOpen(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              <Save size={14} />
              {saving ? 'Saving...' : editingTool ? 'Update Tool' : 'Create Tool'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteTool(null); }}
        title="Delete Tool"
        size="small"
      >
        <p style={{ marginBottom: '16px', color: '#374151' }}>
          Are you sure you want to delete <strong>{deleteTool?.name}</strong>? This cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={() => { setDeleteModalOpen(false); setDeleteTool(null); }}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm}>Delete</button>
        </div>
      </Modal>

      {/* Category Form Modal */}
      <Modal
        isOpen={categoryFormOpen}
        onClose={() => setCategoryFormOpen(false)}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        size="small"
      >
        <div className="form-group" style={{ marginBottom: '16px' }}>
          <label>Category Name</label>
          <input
            type="text"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            placeholder="Category name"
            onKeyDown={(e) => e.key === 'Enter' && handleCategorySave()}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button className="btn-cancel" onClick={() => setCategoryFormOpen(false)}>Cancel</button>
          <button className="btn-primary" onClick={handleCategorySave}>
            <Save size={14} />
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default ToolsManage;
